import { useState, useCallback, useRef, useEffect } from 'react';
import api from '../../api/client';
import { formatPrice } from '../../utils/formatPrice';

const QUICK_AMOUNTS = [10, 25, 50, 100];

export default function GiftCardCreate({ onClose }) {
  const [code, setCode] = useState('');
  const [amount, setAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdCard, setCreatedCard] = useState(null);

  const codeInputRef = useRef(null);

  // Auto-focus the code input so barcode scanner output lands here.
  // Also block onscan.js from intercepting keypress events on this input.
  useEffect(() => {
    const timer = setTimeout(() => codeInputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  const codeRef = useCallback((el) => {
    codeInputRef.current = el;
    if (el) {
      el.addEventListener('keypress', (e) => e.stopPropagation());
    }
  }, []);

  const handleCreate = useCallback(async () => {
    if (!code.trim()) {
      setError('Scan or enter a gift card code.');
      codeInputRef.current?.focus();
      return;
    }
    const value = parseFloat(amount);
    if (!value || value <= 0) {
      setError('Please enter a valid amount.');
      return;
    }

    setError('');
    setIsLoading(true);
    try {
      const result = await api.loadGiftCard({
        code: code.trim(),
        amount: value,
      });
      setCreatedCard({ code: code.trim(), balance: result.balance, loadedAmount: value });
    } catch (err) {
      setError(err?.message || 'Failed to load gift card.');
    } finally {
      setIsLoading(false);
    }
  }, [code, amount]);

  const handlePrint = useCallback(() => {
    if (!createdCard) return;

    const printContent = `
      <html>
      <head>
        <title>Gift Card</title>
        <style>
          body { font-family: system-ui, -apple-system, sans-serif; text-align: center; padding: 40px; }
          .code { font-size: 28px; font-weight: bold; letter-spacing: 2px; margin: 24px 0; font-family: monospace; }
          .amount { font-size: 22px; color: #059669; margin-bottom: 12px; }
          .label { font-size: 14px; color: #6b7280; margin-bottom: 4px; }
          .brand { font-size: 18px; font-weight: 600; margin-bottom: 24px; }
        </style>
      </head>
      <body>
        <div class="brand">Gift Card</div>
        <div class="label">Card Code</div>
        <div class="code">${createdCard.code}</div>
        <div class="label">Value</div>
        <div class="amount">${formatPrice(createdCard.loadedAmount)}</div>
      </body>
      </html>
    `;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    iframe.contentDocument.open();
    iframe.contentDocument.write(printContent);
    iframe.contentDocument.close();

    iframe.contentWindow.onafterprint = () => {
      document.body.removeChild(iframe);
    };

    setTimeout(() => {
      iframe.contentWindow.print();
    }, 250);
  }, [createdCard]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Activate Gift Card"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Activate Gift Card</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {createdCard ? (
            /* Success view */
            <div className="text-center space-y-4">
              <div className="w-12 h-12 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-green-600">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Gift Card Activated</p>
                <p className="text-2xl font-bold font-mono tracking-wider text-gray-900">
                  {createdCard.code}
                </p>
              </div>
              <p className="text-lg font-semibold text-green-600">
                {formatPrice(createdCard.loadedAmount)}
              </p>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handlePrint}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Print Receipt
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-pos-primary text-white text-sm font-semibold hover:bg-pos-primary-dark transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            /* Activate form */
            <>
              {/* Card code — scan or type */}
              <div>
                <label htmlFor="gc-code" className="block text-sm font-medium text-gray-700 mb-1">
                  Card Code
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4V1m0 22v-3M4 12H1m22 0h-3" />
                      <rect x="5" y="5" width="14" height="14" rx="1" strokeLinecap="round" strokeLinejoin="round" />
                      <line x1="9" y1="9" x2="9" y2="15" strokeLinecap="round" />
                      <line x1="12" y1="9" x2="12" y2="15" strokeLinecap="round" />
                      <line x1="15" y1="9" x2="15" y2="15" strokeLinecap="round" />
                    </svg>
                  </div>
                  <input
                    ref={codeRef}
                    id="gc-code"
                    type="text"
                    value={code}
                    onChange={(e) => { setCode(e.target.value); setError(''); }}
                    onPaste={(e) => e.stopPropagation()}
                    placeholder="Scan barcode or type code"
                    className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pos-primary/30 focus:border-pos-primary transition-colors"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Scan the barcode on the physical card or type the code manually
                </p>
              </div>

              {/* Amount */}
              <div>
                <label htmlFor="gc-amount" className="block text-sm font-medium text-gray-700 mb-2">
                  Amount to Load
                </label>
                <div className="flex gap-2 mb-2">
                  {QUICK_AMOUNTS.map((val) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setAmount(String(val))}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        parseFloat(amount) === val
                          ? 'border-pos-primary bg-pos-secondary text-pos-primary-dark'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {formatPrice(val)}
                    </button>
                  ))}
                </div>
                <input
                  id="gc-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); setError(''); }}
                  placeholder="Custom amount"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pos-primary/30 focus:border-pos-primary transition-colors"
                />
              </div>

              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}

              <button
                type="button"
                onClick={handleCreate}
                disabled={isLoading || !code.trim() || !amount}
                className="w-full px-4 py-3 rounded-lg bg-pos-primary text-white text-sm font-semibold hover:bg-pos-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Activating...
                  </>
                ) : (
                  'Activate Gift Card'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
